import {canReassignTasks} from '../../TaskManagement/taskTimerState';
import {workingSet} from './mergeContext';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from '../find';
import User from '../../entities/user';
import Blackout from '../../entities/blackout';
import Matter from '../../entities/matter';
import Deadline from '../../entities/deadline';
import Conflict from '../../entities/conflict';
import Expense from '../../entities/expense';
import Group from '../../entities/group';
import Role from '../../entities/role';
import TemplateTask from '../../entities/templateTask';
import TimeOff from '../../entities/timeOff';
import Task from '../../entities/task';
import {canCascadeDelete, addDeleteImpact, deleteImpactContext, deleteImpactCounts, workingRows, enqueueDelete} from './deleteContext';
import type {DeleteImpact, DeleteImpactContext, DeleteResult, DeleteTraversal} from './deleteContext';
import {deleteTemplateTask, collectTemplateTaskDeleteImpact} from './templateTaskDeps';
export function canDeleteUser(): boolean {
return canCascadeDelete();
}
export function collectUserDeleteImpact(user: User, context: DeleteImpactContext): void {
const key = 'user:' + user.id;
if (context.visited.has(key)) return;
context.visited.add(key);
const state = store.getState();
const blackoutUserIdImpact = workingRows<Blackout>('blackout', findAll<Blackout>('blackouts')).filter(blackoutRef => blackoutRef.userId === user.id);
blackoutUserIdImpact.forEach(blackoutRef => addDeleteImpact(context, 'blackout:' + blackoutRef.id, 'blackouts'));
const matterAssignedToImpact = workingRows<Matter>('matter', findAll<Matter>('matters')).filter(matterRef => matterRef.assignedTo?.includes(user.id));
matterAssignedToImpact.forEach(matterRef => addDeleteImpact(context, 'matter:' + matterRef.id, 'matters'));
const deadlineCompletedByImpact = workingRows<Deadline>('deadline', findAll<Deadline>('deadlines')).filter(deadlineRef => deadlineRef.completedBy === user.id);
deadlineCompletedByImpact.forEach(deadlineRef => addDeleteImpact(context, 'deadline:' + deadlineRef.id, 'deadlines'));
const conflictUserIdImpact = workingRows<Conflict>('conflict', findAll<Conflict>('conflicts')).filter(conflictRef => conflictRef.userId === user.id);
conflictUserIdImpact.forEach(conflictRef => addDeleteImpact(context, 'conflict:' + conflictRef.id, 'conflicts'));
const conflictUserIdsImpact = workingRows<Conflict>('conflict', findAll<Conflict>('conflicts')).filter(conflictRef => conflictRef.userIds?.includes(user.id));
conflictUserIdsImpact.forEach(conflictRef => addDeleteImpact(context, 'conflict:' + conflictRef.id, 'conflicts'));
const expenseUserIdImpact = workingRows<Expense>('expense', findAll<Expense>('expenses')).filter(expenseRef => expenseRef.userId === user.id);
expenseUserIdImpact.forEach(expenseRef => addDeleteImpact(context, 'expense:' + expenseRef.id, 'expenses'));
const groupUserIdsImpact = workingRows<Group>('group', findAll<Group>('groups')).filter(groupRef => groupRef.userIds?.includes(user.id));
groupUserIdsImpact.forEach(groupRef => addDeleteImpact(context, 'group:' + groupRef.id, 'groups'));
const roleUsersImpact = workingRows<Role>('role', findAll<Role>('roles')).filter(roleRef => roleRef.users?.includes(user.id));
roleUsersImpact.forEach(roleRef => addDeleteImpact(context, 'role:' + roleRef.id, 'roles'));
const templateTaskFavoritedByImpact = workingRows<TemplateTask>('templateTask', findAll<TemplateTask>('templateTasks')).filter(templateTaskRef => templateTaskRef.favoritedBy?.includes(user.id));
templateTaskFavoritedByImpact.forEach(templateTaskRef => addDeleteImpact(context, 'templateTask:' + templateTaskRef.id, 'templateTasks'));
const timeOffUserIdImpact = workingRows<TimeOff>('timeOff', findAll<TimeOff>('timeOffs')).filter(timeOffRef => timeOffRef.userId === user.id);
timeOffUserIdImpact.forEach(timeOffRef => addDeleteImpact(context, 'timeOff:' + timeOffRef.id, 'timeOffs'));
const timeOffApprovedByImpact = workingRows<TimeOff>('timeOff', findAll<TimeOff>('timeOffs')).filter(timeOffRef => timeOffRef.approvedBy === user.id);
timeOffApprovedByImpact.forEach(timeOffRef => addDeleteImpact(context, 'timeOff:' + timeOffRef.id, 'timeOffs'));const deadlineAssignedToTypeImpact = workingRows<Deadline>('deadline', findAll<Deadline>('deadlines')).filter(deadlineRef => deadlineRef.assignedToType === 'U' && deadlineRef.assignedTo === user.id && deadlineRef.status !== 'D');
deadlineAssignedToTypeImpact.forEach(deadline => addDeleteImpact(context, 'deadline:' + deadline.id, 'deadlines'));
const taskAssignedToTypeImpact = workingRows<Task>('task', findAll<Task>('tasks')).filter(taskRef => taskRef.assignedToType === 'U' && taskRef.assignedTo === user.id && taskRef.status !== 'D');
taskAssignedToTypeImpact.forEach(task => addDeleteImpact(context, 'task:' + task.id, 'tasks'));
workingRows<TemplateTask>('templateTask', findAll<TemplateTask>('templateTasks')).filter(templateTask => templateTask.status !== 'D' && ['U'].includes(templateTask.assignedToType || '') && templateTask.assignedTo === user.id).forEach(templateTask => {
if (context.visited.has('templateTask:' + templateTask.id)) return;
addDeleteImpact(context, 'templateTask:' + templateTask.id, 'templateTasks');
collectTemplateTaskDeleteImpact(templateTask, context);
});
}
export function deleteImpactUser(user: User): DeleteImpact {
const context = deleteImpactContext();
collectUserDeleteImpact(user, context);
return deleteImpactCounts(context);
}
export function deleteUser(user: User, mergeId?: string, cascade?: DeleteTraversal): DeleteResult {
if (!cascade) {
if (!canDeleteUser()) return {status: 'notReady', impacts: []};
const context = deleteImpactContext();
collectUserDeleteImpact(user, context);
if (context.blocked) return {status: 'blocked', impacts: deleteImpactCounts(context)};
cascade = {visited: new Set(), importSource: mergeId ? 'merge' : 'delete', importId: mergeId || user.id};
}
const key = 'user:' + user.id;
if (cascade.visited.has(key)) return {status: 'deleted', impacts: []};
cascade.visited.add(key);
const targetImportSource = cascade.importSource;
mergeId = cascade.importId;
const _work = workingSet();
const dispatch = (action: any, key: string) => enqueueDelete(_work, onQueued => store.dispatch({...action, onQueued: (saved: boolean) => onQueued(saved, action.type.endsWith('_REMOVE_QUEUE'))}), () => _work.delete(key));
workingRows<TemplateTask>('templateTask', findAll<TemplateTask>('templateTasks')).filter(templateTask => templateTask.status !== 'D' && ['U'].includes(templateTask.assignedToType || '') && templateTask.assignedTo === user.id).forEach(templateTask => {deleteTemplateTask(templateTask, mergeId, cascade);});
workingRows<Matter>('matter', [...store.getState().matters.getMappingFor('userId', user.id)]).filter(matterTemp => matterTemp.assignedTo?.includes(user.id)).forEach(matterTemp => {
const _matter = ((_work.get('matter:' + matterTemp.id) ?? matterTemp) as Matter).clone();
if (_matter.status === 'D') return;
_matter.status = 'U';
_matter.addedOn = newHRTimestamp();
_matter.addedBy = getCurrentUserId();
_matter.importSource = targetImportSource;
_matter.importId = mergeId;
_matter.assignedTo = _matter.assignedTo?.filter(id => id !== user.id) || [];

_work.set('matter:' + _matter.id, _matter);
dispatch(Actions.MatterActions.saveMatterQueue(_matter), 'matter:' + _matter.id);
});
workingRows<Deadline>('deadline', [...store.getState().deadlines.getMappingFor('userId', user.id)]).filter(deadlineTemp => deadlineTemp.completedBy === user.id).forEach(deadlineTemp => {
const _deadline = ((_work.get('deadline:' + deadlineTemp.id) ?? deadlineTemp) as Deadline).clone();
if (_deadline.status === 'D') return;
_deadline.status = 'U';
_deadline.addedOn = newHRTimestamp();
_deadline.addedBy = getCurrentUserId();
_deadline.importSource = targetImportSource;
_deadline.importId = mergeId;
if (_deadline.completedBy === user.id) {
_deadline.completedBy = null;

}
_work.set('deadline:' + _deadline.id, _deadline);
dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline), 'deadline:' + _deadline.id);
});
workingRows<Conflict>('conflict', [...store.getState().conflicts.getMappingFor('userIds', user.id)]).filter(conflictTemp => conflictTemp.userIds?.includes(user.id)).forEach(conflictTemp => {
const _conflict = ((_work.get('conflict:' + conflictTemp.id) ?? conflictTemp) as Conflict).clone();
if (_conflict.status === 'D') return;
_conflict.status = 'U';
_conflict.addedOn = newHRTimestamp();
_conflict.addedBy = getCurrentUserId();
_conflict.importSource = targetImportSource;
_conflict.importId = mergeId;
_conflict.userIds = _conflict.userIds?.filter(id => id !== user.id) || [];

_work.set('conflict:' + _conflict.id, _conflict);
dispatch(Actions.ConflictActions.saveConflictQueue(_conflict), 'conflict:' + _conflict.id);
});
workingRows<Expense>('expense', [...store.getState().expenses.getMappingFor('userId', user.id)]).filter(expenseTemp => expenseTemp.userId === user.id).forEach(expenseTemp => {
const _expense = ((_work.get('expense:' + expenseTemp.id) ?? expenseTemp) as Expense).clone();
if (_expense.status === 'D') return;
_expense.status = 'U';
_expense.addedOn = newHRTimestamp();
_expense.addedBy = getCurrentUserId();
_expense.importSource = targetImportSource;
_expense.importId = mergeId;
if (_expense.userId === user.id) {
_expense.userId = null;

}
_work.set('expense:' + _expense.id, _expense);
dispatch(Actions.ExpenseActions.saveExpenseQueue(_expense), 'expense:' + _expense.id);
});
workingRows<Group>('group', [...store.getState().groups.getMappingFor('userIds', user.id)]).filter(groupTemp => groupTemp.userIds?.includes(user.id)).forEach(groupTemp => {
const _group = ((_work.get('group:' + groupTemp.id) ?? groupTemp) as Group).clone();
if (_group.status === 'D') return;
_group.status = 'U';
_group.addedOn = newHRTimestamp();
_group.addedBy = getCurrentUserId();
_group.importSource = targetImportSource;
_group.importId = mergeId;
_group.userIds = _group.userIds?.filter(id => id !== user.id) || [];

_work.set('group:' + _group.id, _group);
dispatch(Actions.GroupActions.saveGroupQueue(_group), 'group:' + _group.id);
});
workingRows<Role>('role', [...store.getState().roles.getMappingFor('users', user.id)]).filter(roleTemp => roleTemp.users?.includes(user.id)).forEach(roleTemp => {
const _role = ((_work.get('role:' + roleTemp.id) ?? roleTemp) as Role).clone();
if (_role.status === 'D') return;
_role.status = 'U';
_role.addedOn = newHRTimestamp();
_role.addedBy = getCurrentUserId();
_role.importSource = targetImportSource;
_role.importId = mergeId;
_role.users = _role.users?.filter(id => id !== user.id) || [];

_work.set('role:' + _role.id, _role);
dispatch(Actions.RoleActions.saveRoleQueue(_role), 'role:' + _role.id);
});
workingRows<TemplateTask>('templateTask', [...store.getState().templateTasks.getMappingFor('favoritedBy', user.id)]).filter(templateTaskTemp => templateTaskTemp.favoritedBy?.includes(user.id)).forEach(templateTaskTemp => {
const _templateTask = ((_work.get('templateTask:' + templateTaskTemp.id) ?? templateTaskTemp) as TemplateTask).clone();
if (_templateTask.status === 'D') return;
_templateTask.status = 'U';
_templateTask.addedOn = newHRTimestamp();
_templateTask.addedBy = getCurrentUserId();
_templateTask.importSource = targetImportSource;
_templateTask.importId = mergeId;
_templateTask.favoritedBy = _templateTask.favoritedBy?.filter(id => id !== user.id) || [];

_work.set('templateTask:' + _templateTask.id, _templateTask);
dispatch(Actions.TemplateTaskActions.saveTemplateTaskQueue(_templateTask), 'templateTask:' + _templateTask.id);
});
workingRows<TimeOff>('timeOff', [...store.getState().timeOffs.getMappingFor('approvedBy', user.id)]).filter(timeOffTemp => timeOffTemp.approvedBy === user.id).forEach(timeOffTemp => {
const _timeOff = ((_work.get('timeOff:' + timeOffTemp.id) ?? timeOffTemp) as TimeOff).clone();
if (_timeOff.status === 'D') return;
_timeOff.status = 'U';
_timeOff.addedOn = newHRTimestamp();
_timeOff.addedBy = getCurrentUserId();
_timeOff.importSource = targetImportSource;
_timeOff.importId = mergeId;
if (_timeOff.approvedBy === user.id) {
_timeOff.approvedBy = null;

}
_work.set('timeOff:' + _timeOff.id, _timeOff);
dispatch(Actions.TimeOffActions.saveTimeOffQueue(_timeOff), 'timeOff:' + _timeOff.id);
});workingRows<Deadline>('deadline', findAll<Deadline>('deadlines')).filter(deadlineTemp => deadlineTemp.assignedToType === 'U' && deadlineTemp.assignedTo === user.id && deadlineTemp.status !== 'D').forEach(deadlineTemp => {
const _deadline = ((_work.get('deadline:' + deadlineTemp.id) ?? deadlineTemp) as Deadline).clone();
if (_deadline.status === 'D') return;
_deadline.assignedTo = null;
_deadline.status = 'U';
_deadline.addedOn = newHRTimestamp();
_deadline.addedBy = getCurrentUserId();
_deadline.importSource = targetImportSource;
_deadline.importId = mergeId;
_work.set('deadline:' + _deadline.id, _deadline);
dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline), 'deadline:' + _deadline.id);
});
workingRows<Task>('task', findAll<Task>('tasks')).filter(taskTemp => taskTemp.assignedToType === 'U' && taskTemp.assignedTo === user.id && taskTemp.status !== 'D').filter(canReassignTasks).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.assignedTo = null;
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
_work.set('task:' + _task.id, _task);
dispatch(Actions.TaskActions.saveTaskQueue(_task), 'task:' + _task.id);
});

const deleted = user.clone();
deleted.status = 'D';
deleted.addedOn = newHRTimestamp();
deleted.addedBy = getCurrentUserId();
deleted.importSource = targetImportSource;
deleted.importId = mergeId;
_work.set(key, deleted);
const completion = dispatch(Actions.UserActions.removeUserQueue(deleted), key);
return {status: 'deleted', impacts: [], completion};
}

export function replaceUser(user: User, replaceWith: User, mergeId?: string) {
let targetImportSource = 'merge';
if (!mergeId) {targetImportSource = 'delete';mergeId = user.id;}
const _work = workingSet();
workingRows<Blackout>('blackout', [...store.getState().blackouts.getMappingFor('userId', user.id)]).filter(blackoutTemp => blackoutTemp.userId === user.id).forEach(blackoutTemp => {
const _blackout = ((_work.get('blackout:' + blackoutTemp.id) ?? blackoutTemp) as Blackout).clone();
if (_blackout.status === 'D') return;
_blackout.status = 'U';
_blackout.addedOn = newHRTimestamp();
_blackout.addedBy = getCurrentUserId();
_blackout.importSource = targetImportSource;
_blackout.importId = mergeId;
if (_blackout.userId === user.id) {
_blackout.userId = replaceWith.id;

}
_work.set('blackout:' + _blackout.id, _blackout);
store.dispatch(Actions.BlackoutActions.saveBlackoutQueue(_blackout));
});
workingRows<Matter>('matter', [...store.getState().matters.getMappingFor('userId', user.id)]).filter(matterTemp => matterTemp.assignedTo?.includes(user.id)).forEach(matterTemp => {
const _matter = ((_work.get('matter:' + matterTemp.id) ?? matterTemp) as Matter).clone();
if (_matter.status === 'D') return;
_matter.status = 'U';
_matter.addedOn = newHRTimestamp();
_matter.addedBy = getCurrentUserId();
_matter.importSource = targetImportSource;
_matter.importId = mergeId;
_matter.assignedTo = _matter.assignedTo?.map(id => (id === user.id) ? replaceWith.id : id) || [];

_work.set('matter:' + _matter.id, _matter);
store.dispatch(Actions.MatterActions.saveMatterQueue(_matter));
});
workingRows<Deadline>('deadline', [...store.getState().deadlines.getMappingFor('userId', user.id)]).filter(deadlineTemp => deadlineTemp.completedBy === user.id).forEach(deadlineTemp => {
const _deadline = ((_work.get('deadline:' + deadlineTemp.id) ?? deadlineTemp) as Deadline).clone();
if (_deadline.status === 'D') return;
_deadline.status = 'U';
_deadline.addedOn = newHRTimestamp();
_deadline.addedBy = getCurrentUserId();
_deadline.importSource = targetImportSource;
_deadline.importId = mergeId;
if (_deadline.completedBy === user.id) {
_deadline.completedBy = replaceWith.id;

}
_work.set('deadline:' + _deadline.id, _deadline);
store.dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline));
});
workingRows<Conflict>('conflict', [...store.getState().conflicts.getMappingFor('userId', user.id), ...store.getState().conflicts.getMappingFor('userIds', user.id)]).filter(conflictTemp => conflictTemp.userId === user.id || conflictTemp.userIds?.includes(user.id)).forEach(conflictTemp => {
const _conflict = ((_work.get('conflict:' + conflictTemp.id) ?? conflictTemp) as Conflict).clone();
if (_conflict.status === 'D') return;
_conflict.status = 'U';
_conflict.addedOn = newHRTimestamp();
_conflict.addedBy = getCurrentUserId();
_conflict.importSource = targetImportSource;
_conflict.importId = mergeId;
if (_conflict.userId === user.id) {
_conflict.userId = replaceWith.id;

}
_conflict.userIds = _conflict.userIds?.map(id => (id === user.id) ? replaceWith.id : id) || [];

_work.set('conflict:' + _conflict.id, _conflict);
store.dispatch(Actions.ConflictActions.saveConflictQueue(_conflict));
});
workingRows<Expense>('expense', [...store.getState().expenses.getMappingFor('userId', user.id)]).filter(expenseTemp => expenseTemp.userId === user.id).forEach(expenseTemp => {
const _expense = ((_work.get('expense:' + expenseTemp.id) ?? expenseTemp) as Expense).clone();
if (_expense.status === 'D') return;
_expense.status = 'U';
_expense.addedOn = newHRTimestamp();
_expense.addedBy = getCurrentUserId();
_expense.importSource = targetImportSource;
_expense.importId = mergeId;
if (_expense.userId === user.id) {
_expense.userId = replaceWith.id;

}
_work.set('expense:' + _expense.id, _expense);
store.dispatch(Actions.ExpenseActions.saveExpenseQueue(_expense));
});
workingRows<Group>('group', [...store.getState().groups.getMappingFor('userIds', user.id)]).filter(groupTemp => groupTemp.userIds?.includes(user.id)).forEach(groupTemp => {
const _group = ((_work.get('group:' + groupTemp.id) ?? groupTemp) as Group).clone();
if (_group.status === 'D') return;
_group.status = 'U';
_group.addedOn = newHRTimestamp();
_group.addedBy = getCurrentUserId();
_group.importSource = targetImportSource;
_group.importId = mergeId;
_group.userIds = _group.userIds?.map(id => (id === user.id) ? replaceWith.id : id) || [];

_work.set('group:' + _group.id, _group);
store.dispatch(Actions.GroupActions.saveGroupQueue(_group));
});
workingRows<Role>('role', [...store.getState().roles.getMappingFor('users', user.id)]).filter(roleTemp => roleTemp.users?.includes(user.id)).forEach(roleTemp => {
const _role = ((_work.get('role:' + roleTemp.id) ?? roleTemp) as Role).clone();
if (_role.status === 'D') return;
_role.status = 'U';
_role.addedOn = newHRTimestamp();
_role.addedBy = getCurrentUserId();
_role.importSource = targetImportSource;
_role.importId = mergeId;
_role.users = _role.users?.map(id => (id === user.id) ? replaceWith.id : id) || [];

_work.set('role:' + _role.id, _role);
store.dispatch(Actions.RoleActions.saveRoleQueue(_role));
});
workingRows<TemplateTask>('templateTask', [...store.getState().templateTasks.getMappingFor('favoritedBy', user.id)]).filter(templateTaskTemp => templateTaskTemp.favoritedBy?.includes(user.id)).forEach(templateTaskTemp => {
const _templateTask = ((_work.get('templateTask:' + templateTaskTemp.id) ?? templateTaskTemp) as TemplateTask).clone();
if (_templateTask.status === 'D') return;
_templateTask.status = 'U';
_templateTask.addedOn = newHRTimestamp();
_templateTask.addedBy = getCurrentUserId();
_templateTask.importSource = targetImportSource;
_templateTask.importId = mergeId;
_templateTask.favoritedBy = _templateTask.favoritedBy?.map(id => (id === user.id) ? replaceWith.id : id) || [];

_work.set('templateTask:' + _templateTask.id, _templateTask);
store.dispatch(Actions.TemplateTaskActions.saveTemplateTaskQueue(_templateTask));
});
workingRows<TimeOff>('timeOff', [...store.getState().timeOffs.getMappingFor('userId', user.id), ...store.getState().timeOffs.getMappingFor('approvedBy', user.id)]).filter(timeOffTemp => timeOffTemp.userId === user.id || timeOffTemp.approvedBy === user.id).forEach(timeOffTemp => {
const _timeOff = ((_work.get('timeOff:' + timeOffTemp.id) ?? timeOffTemp) as TimeOff).clone();
if (_timeOff.status === 'D') return;
_timeOff.status = 'U';
_timeOff.addedOn = newHRTimestamp();
_timeOff.addedBy = getCurrentUserId();
_timeOff.importSource = targetImportSource;
_timeOff.importId = mergeId;
if (_timeOff.userId === user.id) {
_timeOff.userId = replaceWith.id;

}
if (_timeOff.approvedBy === user.id) {
_timeOff.approvedBy = replaceWith.id;

}
_work.set('timeOff:' + _timeOff.id, _timeOff);
store.dispatch(Actions.TimeOffActions.saveTimeOffQueue(_timeOff));
});
workingRows<Deadline>('deadline', findAll<Deadline>('deadlines')).filter(deadlineTemp => deadlineTemp.assignedToType === 'U' && deadlineTemp.assignedTo === user.id && deadlineTemp.status !== 'D').forEach(deadlineTemp => {
const _deadline = ((_work.get('deadline:' + deadlineTemp.id) ?? deadlineTemp) as Deadline).clone();
if (_deadline.status === 'D') return;
_deadline.assignedTo = replaceWith.id;
_deadline.status = 'U';
_deadline.addedOn = newHRTimestamp();
_deadline.addedBy = getCurrentUserId();
_deadline.importSource = targetImportSource;
_deadline.importId = mergeId;
_work.set('deadline:' + _deadline.id, _deadline);
store.dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline));
});
workingRows<Task>('task', findAll<Task>('tasks')).filter(taskTemp => taskTemp.assignedToType === 'U' && taskTemp.assignedTo === user.id && taskTemp.status !== 'D').filter(canReassignTasks).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.assignedTo = replaceWith.id;
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
_work.set('task:' + _task.id, _task);
store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
});
workingRows<Matter>('matter', findAll<Matter>('matters')).filter(matterTemp => matterTemp.status !== 'D' && matterTemp.hasOwnProperty('hourlyRates')).filter(matterTemp => !!matterTemp.hourlyRates?.some(item => item.id === user.id)).forEach(matterTemp => {
const _matter = ((_work.get('matter:' + matterTemp.id) ?? matterTemp) as Matter).clone();
if (_matter.status === 'D') return;
if (!!_matter.hourlyRates) {_matter.hourlyRates = [..._matter.hourlyRates].map(f => {if (f.id === user.id) {const _f = {...f}; _f.id = replaceWith.id; return _f;} else return f;});} else {_matter.hourlyRates = [];}
_matter.status = 'U';
_matter.addedOn = newHRTimestamp();
_matter.addedBy = getCurrentUserId();
_matter.importSource = targetImportSource;
_matter.importId = mergeId;
_work.set('matter:' + _matter.id, _matter);
store.dispatch(Actions.MatterActions.saveMatterQueue(_matter));
});
workingRows<Conflict>('conflict', findAll<Conflict>('conflicts')).filter(conflictTemp => conflictTemp.status !== 'D' && conflictTemp.hasOwnProperty('userSignOffs')).filter(conflictTemp => !!conflictTemp.userSignOffs?.some(item => item.userId === user.id)).forEach(conflictTemp => {
const _conflict = ((_work.get('conflict:' + conflictTemp.id) ?? conflictTemp) as Conflict).clone();
if (_conflict.status === 'D') return;
if (!!_conflict.userSignOffs) {_conflict.userSignOffs = [..._conflict.userSignOffs].map(f => {if (f.userId === user.id) {const _f = {...f}; _f.userId = replaceWith.id; return _f;} else return f;});} else {_conflict.userSignOffs = [];}
_conflict.status = 'U';
_conflict.addedOn = newHRTimestamp();
_conflict.addedBy = getCurrentUserId();
_conflict.importSource = targetImportSource;
_conflict.importId = mergeId;
_work.set('conflict:' + _conflict.id, _conflict);
store.dispatch(Actions.ConflictActions.saveConflictQueue(_conflict));
});

workingRows<TemplateTask>('templateTask', findAll<TemplateTask>('templateTasks')).filter(templateTask => templateTask.status !== 'D' && ['U'].includes(templateTask.assignedToType || '') && templateTask.assignedTo === user.id).forEach(templateTaskTemp => {
const _templateTask = ((_work.get('templateTask:' + templateTaskTemp.id) ?? templateTaskTemp) as TemplateTask).clone();
if (_templateTask.status === 'D') return;
_templateTask.assignedTo = replaceWith.id;
_templateTask.status = 'U';
_templateTask.addedOn = newHRTimestamp();
_templateTask.addedBy = getCurrentUserId();
_templateTask.importSource = targetImportSource;
_templateTask.importId = mergeId;
_work.set('templateTask:' + _templateTask.id, _templateTask);
store.dispatch(Actions.TemplateTaskActions.saveTemplateTaskQueue(_templateTask));
});
return user;
}
